import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HiOutlineHashtag, HiOutlineLightningBolt, HiOutlineLockClosed } from 'react-icons/hi';
import PageForm from '../../templates/PageForm';
import BadgeStatus from '../../atoms/BadgeStatus';
import RoutingDiagram from './routing/RoutingDiagram';
import RoutingMappingTable from './routing/RoutingMappingTable';
import RoutingViewSkeleton from './routing/RoutingViewSkeleton';
import communicationApi from '../../../services/communicationApi';
import { MessageComposition, Routing, Template } from '../../../types/communication';

type RoutingTab = 'flow' | 'mapping' | 'definition';

const ENV_ORDER = ['sandbox', 'dev', 'hml', 'prd'];
const ENV_LABEL: Record<string, string> = { sandbox: 'SANDBOX', dev: 'DEV', hml: 'HML', prd: 'PRD' };
const envLabel = (env: string): string => ENV_LABEL[env] ?? env.toUpperCase();
const sortEnvironments = (envs: string[]): string[] =>
    [...envs].sort((a, b) => {
        const ia = ENV_ORDER.indexOf(a);
        const ib = ENV_ORDER.indexOf(b);
        return (ia === -1 ? ENV_ORDER.length : ia) - (ib === -1 ? ENV_ORDER.length : ib);
    });

const TABS: { key: RoutingTab; label: string }[] = [
    { key: 'flow', label: 'Fluxo' },
    { key: 'mapping', label: 'Mapeamento de campos' },
    { key: 'definition', label: 'Definição' },
];

const SummaryCard = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        <div className="mt-1 text-sm text-gray-800 dark:text-gray-100">{children}</div>
    </div>
);

const RoutingViewPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [routing, setRouting] = useState<Routing | null>(null);
    const [metas, setMetas] = useState<MessageComposition[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [apiVersion, setApiVersion] = useState('');
    const [tab, setTab] = useState<RoutingTab>('flow');

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        communicationApi
            .getRouting(id)
            .then(async (r) => {
                setRouting(r);
                const compositionIds = Array.from(
                    new Set(r.targets.map((t) => t.messageCompositionId).filter(Boolean)),
                ) as string[];
                const [metaResults, templatePage] = await Promise.all([
                    Promise.allSettled(compositionIds.map((cid) => communicationApi.getMessageComposition(cid))),
                    communicationApi.listTemplates({ pageSize: 9999 }),
                ]);
                const fullMetas = metaResults
                    .filter((res): res is PromiseFulfilledResult<MessageComposition> => res.status === 'fulfilled')
                    .map((res) => res.value);
                if (fullMetas.length < compositionIds.length) {
                    toast.warn('Uma ou mais composições referenciadas não foram encontradas e ficaram de fora da visão.');
                }
                setMetas(fullMetas);
                setTemplates(templatePage.list);
                setApiVersion(`${templatePage.apiName} - ${templatePage.apiVersion}`);
            })
            .catch(() => toast.error('Roteamento não encontrado.'))
            .finally(() => setLoading(false));
    }, [id]);

    const r = routing;

    const slackChannelsByEnv = useMemo(() => {
        const byEnv = new Map<string, Set<string>>();
        if (!r) return byEnv;
        r.targets.forEach((target) => {
            if (target.channel !== 'INTERNAL_MESSAGE') return;
            const meta = metas.find((m) => m.id === target.messageCompositionId);
            const config = meta?.channelConfig;
            if (!config || config.kind !== 'INTERNAL_MESSAGE') return;
            config.variants.forEach((variant) => {
                if (!variant.channel.trim()) return;
                variant.environments.forEach((env) => {
                    if (!byEnv.has(env)) byEnv.set(env, new Set());
                    byEnv.get(env)!.add(variant.channel);
                });
            });
        });
        return byEnv;
    }, [r, metas]);

    const hasSlackTarget = (r?.targets ?? []).some((t) => t.channel === 'INTERNAL_MESSAGE');

    const rolloutEnvironments = useMemo(() => {
        if (!r) return [];
        if (r.environments.length) return sortEnvironments(r.environments);
        const fromVariants = Array.from(slackChannelsByEnv.keys());
        return sortEnvironments(fromVariants.length ? fromVariants : ENV_ORDER);
    }, [r, slackChannelsByEnv]);

    return (
        <PageForm
            pageTitle={r ? r.event : 'Roteamento'}
            apiVersion={apiVersion}
            isLoading={loading}
            breadcrumb={[
                { label: 'Roteamento', url: '/communication/routings' },
                { label: r?.ruleId ?? '', url: '#' },
            ]}
            controlSlot={
                <div className="flex items-center gap-3 m-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <HiOutlineLockClosed size={14} /> Somente leitura — definido pelo time de engenharia
                    </span>
                    <span className="flex-1" />
                    <button
                        className="px-4 py-2 rounded-md text-sm bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100"
                        onClick={() => navigate('/communication/routings')}
                    >
                        Voltar
                    </button>
                </div>
            }
        >
            {loading && <RoutingViewSkeleton />}
            {!loading && r && (
                <div className="py-4 flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <SummaryCard label="Quando acontece">
                            <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                                <HiOutlineLightningBolt className="text-amber-500" size={16} />
                                {r.event}
                            </span>
                        </SummaryCard>
                        <SummaryCard label="Condições">
                            {r.conditions.length
                                ? r.conditions.map((c) => (
                                      <div key={c.jsonPath} className="font-mono text-xs">
                                          {c.jsonPath} = {c.equals}
                                      </div>
                                  ))
                                : 'Sempre que o evento ocorrer'}
                        </SummaryCard>
                        <SummaryCard label="Composições acionadas">
                            {r.targets.length
                                ? r.targets.map((t, i) => (
                                      <div key={i} className="text-xs">
                                          {metas.find((m) => m.id === t.messageCompositionId)?.name ?? t.messageCompositionId}
                                      </div>
                                  ))
                                : 'Nenhuma'}
                        </SummaryCard>
                        <SummaryCard label="Status e ambientes">
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <BadgeStatus color={r.enabled ? 'green' : 'gray'} text={r.enabled ? 'Ativo' : 'Inativo'} />
                                    <span className="text-xs text-gray-500">
                                        {r.environments.length ? sortEnvironments(r.environments).map(envLabel).join(' · ') : 'Todos os ambientes'}
                                    </span>
                                </div>
                                {hasSlackTarget && (
                                    <div className="border-t border-dashed border-gray-200 dark:border-slate-700 pt-2">
                                        <div className="text-[11px] font-semibold text-gray-400 mb-1">Canais do Slack por ambiente</div>
                                        <div className="flex flex-col gap-1">
                                            {rolloutEnvironments.map((env) => {
                                                const channels = Array.from(slackChannelsByEnv.get(env) ?? []);
                                                return (
                                                    <div key={env} className="flex items-start gap-2 text-xs">
                                                        <span className="w-16 shrink-0 font-semibold text-gray-500">{envLabel(env)}</span>
                                                        {channels.length ? (
                                                            <span className="flex flex-wrap gap-1">
                                                                {channels.map((c) => (
                                                                    <span key={c} className="inline-flex items-center gap-0.5 rounded bg-purple-50 text-purple-700 px-1.5 py-0.5 dark:bg-purple-900/40 dark:text-purple-300">
                                                                        <HiOutlineHashtag size={11} />
                                                                        {c}
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 italic">redirecionado p/ canal de testes</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </SummaryCard>
                    </div>

                    <div className="flex gap-1 border-b border-gray-200 dark:border-slate-700">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={`px-3 py-2 -mb-px border-b-2 text-sm ${
                                    tab === t.key
                                        ? 'border-teal-500 text-teal-800 dark:text-teal-200 font-medium'
                                        : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === 'flow' && <RoutingDiagram routing={r} messageCompositions={metas} templates={templates} />}
                    {tab === 'mapping' && <RoutingMappingTable routing={r} metas={metas} templates={templates} />}
                    {tab === 'definition' && (
                        <pre className="rounded-xl bg-slate-900 text-slate-100 text-xs p-4 overflow-x-auto">{JSON.stringify(r, null, 2)}</pre>
                    )}
                </div>
            )}
        </PageForm>
    );
};

export default RoutingViewPage;
